import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useCounter, useInterval, usePrevious } from 'react-use';
import { useEnvironment, getBuildInfo } from '#core/Environment';
import UpdateNotification from './UpdateNotification';
import useLogger from '#hooks/useLogger';

const AppUpdateNotification: React.FC<{
  checkInterval?: number;
  failedChecksThreshold?: number;
}> = ({ checkInterval = 1000 * 60 * 50, failedChecksThreshold = 1 }) => {
  const { baseUrl, version: appVersion } = useEnvironment();
  const logger = useLogger();
  const [version, setVersion] = useState<string>(appVersion);
  const previousVersion = usePrevious(version);
  const [checkEnabled, setCheckEnabled] = useState<boolean>(true);
  const [failedChecks, { inc: countFailedCheck }] = useCounter();
  const [showUpdateMessage, setShowUpdateMessage] = useState<boolean>(false);

  const hideUpdateMessage = useCallback(() => setShowUpdateMessage(false), [
    setShowUpdateMessage,
  ]);

  const checkForUpdate = useCallback(async () => {
    try {
      const { data } = await axios.get(baseUrl);
      const htmlEl = document.createElement('html');
      htmlEl.innerHTML = data;
      const { version: versionFromRequest } = getBuildInfo(htmlEl);
      if (versionFromRequest === '') {
        throw new Error('Unable to find version from request');
      }
      if (version !== versionFromRequest) {
        setVersion(versionFromRequest);
      }
    } catch (e) {
      logger.warn(`Version check failed`);

      logger.warn(`${e}`);
      // dont immediately fail since it's possible we got an error due to a new version being deployed at exactly the same time we check
      if (failedChecks >= failedChecksThreshold) {
        logger.warn(
          `Failed version checks ${failedChecks} >= threshold of ${failedChecksThreshold}, stopping version checks`,
        );
        setCheckEnabled(false);
      } else {
        countFailedCheck();
      }
    }
  }, [
    baseUrl,
    countFailedCheck,
    failedChecks,
    failedChecksThreshold,
    logger,
    version,
  ]);

  useEffect(() => {
    if (
      appVersion !== version ||
      (typeof previousVersion !== 'undefined' && previousVersion !== version)
    ) {
      setShowUpdateMessage(true);
    }
  }, [appVersion, previousVersion, version]);

  useInterval(checkForUpdate, checkEnabled ? checkInterval : null);

  return showUpdateMessage ? (
    <UpdateNotification dismissMessage={hideUpdateMessage} />
  ) : null;
};

export default AppUpdateNotification;
